<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useClientStore } from '@/stores/client'
import { imageSizeApi } from '@/api/entities/review/imageSize'

definePage({
  meta: {
    title: '이미지 사이즈',
    action: 'read',
    subject: 'review',
  },
})

const { snackbar, handleError } = useErrorHandler()
const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 상태
const loading = ref(false)
const summary = ref<any>(null)

// 숫자 포맷
const formatNumber = (num: number | null | undefined) => {
  if (num === null || num === undefined)
    return '0'

  return new Intl.NumberFormat('ko-KR').format(num)
}

// 계산된 값
const totalImages = computed(() => {
  if (summary.value)
    return formatNumber(summary.value.total_images)

  return '0'
})

const totalSize = computed(() => {
  if (summary.value)
    return `${formatNumber(summary.value.total_size_in_gb)}GB`

  return '0'
})

const expectedSize = computed(() => {
  if (summary.value)
    return `${formatNumber(summary.value.total_size_in_gb * 0.66)}GB`

  return '0'
})

// 데이터 로드
const getReviewImageSize = async () => {
  if (!selectedClient.value)
    return

  loading.value = true
  try {
    const { success, data, error } = await imageSizeApi.getImageSummary(selectedClient.value.id)

    if (success)
      summary.value = data
    else if (error)
      handleError(error, { notificationMessage: '이미지 정보를 불러오는데 실패했습니다.' })
  }
  catch (error) {
    handleError(error, { notificationMessage: '이미지 정보를 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 이미지 용량 파악 요청
const requestImageSize = async () => {
  if (!selectedClient.value)
    return

  loading.value = true
  try {
    const { success, error } = await imageSizeApi.requestImageSize(selectedClient.value.id)

    if (success) {
      snackbar.success('이미지 용량 파악을 시작합니다.')
      await getReviewImageSize()
    }
    else if (error) {
      handleError(error, { notificationMessage: '이미 처리중입니다.' })
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '요청에 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 감시자
watch(selectedClient, newVal => {
  if (newVal)
    getReviewImageSize()
  else
    summary.value = null
})

onMounted(() => {
  if (selectedClient.value)
    getReviewImageSize()
})
</script>

<template>
  <div>
    <!-- 경고 -->
    <VAlert
      type="warning"
      class="mb-4"
    >
      <VAlertTitle>주의</VAlertTitle>
      이관 예정 리뷰의 이미지 전체 사이즈를 확인합니다. 평점 기준은 제외되어 결과에 나타나지만 블랙홀 기간은 제외되지 않으므로 주의가 필요합니다.
    </VAlert>

    <!-- 고객사 선택 및 액션 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="3"
      >
        <ClientSelector />
      </VCol>
      <VCol>
        <div class="d-flex gap-2">
          <VBtn
            color="primary"
            :loading="loading"
            :disabled="!selectedClient"
            @click="requestImageSize"
          >
            이미지 용량 파악
          </VBtn>
          <VBtn
            variant="outlined"
            :loading="loading"
            :disabled="!selectedClient"
            @click="getReviewImageSize"
          >
            새로고침
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- 통계 카드 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="4"
      >
        <VCard>
          <VCardText class="d-flex align-center justify-space-between">
            <div>
              <p class="text-subtitle-2 mb-1">
                이관 예정 이미지 수
              </p>
              <h4 class="text-h4">
                {{ totalImages }}
              </h4>
            </div>
            <VAvatar
              color="primary"
              variant="tonal"
              rounded
            >
              <VIcon icon="tabler-photo" />
            </VAvatar>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="12"
        md="4"
      >
        <VCard>
          <VCardText class="d-flex align-center justify-space-between">
            <div>
              <p class="text-subtitle-2 mb-1">
                전체 이미지 크기
              </p>
              <h4 class="text-h4">
                {{ totalSize }}
              </h4>
            </div>
            <VAvatar
              color="info"
              variant="tonal"
              rounded
            >
              <VIcon icon="tabler-arrows-maximize" />
            </VAvatar>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="12"
        md="4"
      >
        <VCard>
          <VCardText class="d-flex align-center justify-space-between">
            <div>
              <p class="text-subtitle-2 mb-1">
                압축 후 예상 크기
              </p>
              <h4 class="text-h4">
                {{ expectedSize }}
              </h4>
            </div>
            <VAvatar
              color="success"
              variant="tonal"
              rounded
            >
              <VIcon icon="tabler-arrows-minimize" />
            </VAvatar>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 상세 정보 -->
    <VCard v-if="summary">
      <VCardTitle>상세 정보</VCardTitle>
      <VCardText>
        <VTable>
          <thead>
            <tr>
              <th>항목</th>
              <th>값</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>총 이미지 수</td>
              <td>{{ formatNumber(summary.total_images) }}</td>
            </tr>
            <tr>
              <td>총 크기 (GB)</td>
              <td>{{ formatNumber(summary.total_size_in_gb) }} GB</td>
            </tr>
            <tr>
              <td>총 크기 (MB)</td>
              <td>{{ formatNumber(summary.total_size_in_mb) }} MB</td>
            </tr>
            <tr>
              <td>평균 이미지 크기</td>
              <td>{{ formatNumber(summary.avg_size_in_kb) }} KB</td>
            </tr>
            <tr>
              <td>처리 상태</td>
              <td>
                <VChip
                  :color="summary.status === 'completed' ? 'success' : 'warning'"
                  size="small"
                >
                  {{ summary.status === 'completed' ? '완료' : '처리 중' }}
                </VChip>
              </td>
            </tr>
            <tr v-if="summary.updated_at">
              <td>마지막 업데이트</td>
              <td>{{ new Date(summary.updated_at).toLocaleString('ko-KR') }}</td>
            </tr>
          </tbody>
        </VTable>
      </VCardText>
    </VCard>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </div>
</template>
